/**
 * CommentsSection — native Firestore-backed comments for movies & series.
 *
 * Features:
 *  • Post / delete (owner) / hide (admin)
 *  • Like ❤️ and Love 💖 reactions (per-user, toggleable)
 *  • Threaded replies (1 level deep, expand/collapse per parent)
 *  • Collapse list when > 3 top-level comments (show all / collapse)
 */
import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import {
  MessageCircle,
  Send,
  Trash2,
  EyeOff,
  Eye,
  Shield,
  Loader2,
  User as UserIcon,
  ThumbsUp,
  ThumbsDown,
  Heart,
  Frown,
  Angry,
  Reply,
  ChevronDown,
  ChevronUp,
  Pencil,
  Check,
  X as XIcon,
} from "lucide-react";
import { useUserAuth } from "@/lib/user-auth";
import { useTheme } from "@/lib/theme-context";
import { cn } from "@/lib/utils";
import {
  deleteComment,
  editComment,
  postComment,
  setCommentHidden,
  toggleCommentReaction,
  useComments,
  type Comment,
  type ReactionKind,
} from "@/lib/firebase-data";

type Props = {
  threadId: string;
  title: string;
  className?: string;
  innerClassName?: string;
};

const COLLAPSE_AFTER = 3;
const COMMENT_HASH_PREFIX = "#comment-";
const PENDING_COMMENT_KEY = "rawan:pending-comment-deeplink-v1";
const HEADER_OFFSET = 96;
const DEEPLINK_MAX_TRIES = 24;
const DEEPLINK_RETRY_MS = 120;

type PendingCommentDeepLink = {
  commentId?: string;
  href?: string;
  ts?: number;
};

function decodeHashPart(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readStoredCommentDeepLinkId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_COMMENT_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingCommentDeepLink;
    if (!pending.commentId) return null;
    if (pending.ts && Date.now() - pending.ts > 2 * 60 * 1000) {
      window.sessionStorage.removeItem(PENDING_COMMENT_KEY);
      return null;
    }
    if (pending.href) {
      const target = new URL(pending.href, window.location.origin);
      if (target.pathname !== window.location.pathname) return null;
    }
    return pending.commentId;
  } catch {
    return null;
  }
}

function getCommentHashId(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  if (hash.startsWith(COMMENT_HASH_PREFIX)) return decodeHashPart(hash.slice(COMMENT_HASH_PREFIX.length));
  return readStoredCommentDeepLinkId();
}

function clearStoredCommentDeepLink(commentId: string) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.sessionStorage.getItem(PENDING_COMMENT_KEY);
    if (!raw) return;
    const pending = JSON.parse(raw) as PendingCommentDeepLink;
    if (pending.commentId === commentId) window.sessionStorage.removeItem(PENDING_COMMENT_KEY);
  } catch {
    /* ignore storage errors */
  }
}

function isActuallyVisible(el: HTMLElement): boolean {
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden";
}

function getVisibleElementById(id: string): HTMLElement | null {
  const matches = Array.from(document.querySelectorAll<HTMLElement>("[id]")).filter((el) => el.id === id);
  return matches.find(isActuallyVisible) ?? null;
}

function getVisibleCommentElement(commentId: string): HTMLElement | null {
  return getVisibleElementById(`comment-${commentId}`);
}

function isElementComfortablyVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  return rect.top >= HEADER_OFFSET - 8 && rect.top <= window.innerHeight - Math.min(140, window.innerHeight / 4);
}

function scrollToElement(el: HTMLElement, behavior: ScrollBehavior = "smooth") {
  if (typeof window === "undefined") return;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  const nextTop = Math.max(0, top);
  const scroller = document.scrollingElement ?? document.documentElement;
  try {
    scroller.scrollTo({ top: nextTop, behavior });
  } catch {
    scroller.scrollTop = nextTop;
  }
  window.scrollTo({ top: nextTop, behavior });
}

function timeAgo(ms: number, isAr: boolean): string {
  const diff = Math.max(1, Math.floor((Date.now() - ms) / 1000));
  const units: Array<[number, string, string]> = [
    [60, "ث", "s"],
    [3600, "د", "m"],
    [86400, "س", "h"],
    [86400 * 30, "ي", "d"],
    [86400 * 365, "ش", "mo"],
  ];
  for (let i = 0; i < units.length; i++) {
    const [step, ar, en] = units[i];
    if (diff < step) {
      const prev = i === 0 ? 1 : units[i - 1][0];
      const v = Math.max(1, Math.floor(diff / prev));
      return isAr ? `قبل ${v} ${ar}` : `${v}${en} ago`;
    }
  }
  const y = Math.floor(diff / (86400 * 365));
  return isAr ? `قبل ${y} سنة` : `${y}y ago`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? "").join("").toUpperCase() || "U";
}

function Avatar({ name, src }: { name: string; src?: string | null }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-9 h-9 rounded-full object-cover border border-border shrink-0"
        onError={(e) => {
          // If the stored avatar URL 404s (e.g. expired upload), drop the
          // <img> so the initials fallback below renders on next paint.
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />
    );
  }
  return (
    <div
      aria-hidden
      className="grid place-items-center w-9 h-9 rounded-full text-xs font-bold text-primary-foreground shrink-0"
      style={{ background: "var(--gradient-primary)" }}
    >
      {initials(name)}
    </div>
  );
}

/** All available reactions, in display order. */
const REACTIONS: Array<{
  kind: ReactionKind;
  Icon: React.ComponentType<{ className?: string }>;
  labelAr: string;
  labelEn: string;
  activeClass: string;
  fillClass: string;
}> = [
  { kind: "likes",   Icon: ThumbsUp,   labelAr: "أعجبني", labelEn: "Like",  activeClass: "text-primary",   fillClass: "fill-primary" },
  { kind: "loves",   Icon: Heart,      labelAr: "أحببته", labelEn: "Love",  activeClass: "text-red-500",   fillClass: "fill-red-500" },
  { kind: "sads",    Icon: Frown,      labelAr: "حزين",   labelEn: "Sad",   activeClass: "text-blue-400",  fillClass: "fill-blue-400/40" },
  { kind: "angrys",  Icon: Angry,      labelAr: "أغضبني", labelEn: "Angry", activeClass: "text-orange-500",fillClass: "fill-orange-500/40" },
  { kind: "bads",    Icon: ThumbsDown, labelAr: "سيّئ",   labelEn: "Bad",   activeClass: "text-zinc-500",  fillClass: "fill-zinc-500/50" },
];

function ReactionBar({
  comment,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  size = "md",
  isAr,
}: {
  comment: Comment;
  currentUserId: string | null;
  currentUserName?: string;
  currentUserAvatar?: string | null;
  size?: "sm" | "md";
  isAr: boolean;
}) {
  const sm = size === "sm";
  return (
    <>
      {REACTIONS.map(({ kind, Icon, labelAr, labelEn, activeClass, fillClass }) => {
        const arr = (comment[kind] as string[] | undefined) ?? [];
        const active = !!currentUserId && arr.includes(currentUserId);
        return (
          <button
            key={kind}
            type="button"
            onClick={() => {
              if (!currentUserId) return;
              void toggleCommentReaction(
                comment.id,
                currentUserId,
                kind,
                !active,
                currentUserName
                  ? { displayName: currentUserName, avatar: currentUserAvatar ?? null }
                  : undefined,
              );
            }}
            disabled={!currentUserId}
            title={isAr ? labelAr : labelEn}
            aria-label={isAr ? labelAr : labelEn}
            className={`inline-flex items-center gap-1 ${sm ? "px-1.5 py-0.5" : "px-2 py-1"} rounded-md hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition ${
              active ? `${activeClass} font-bold` : ""
            }`}
          >
            <Icon className={`${sm ? "h-3 w-3" : "h-3.5 w-3.5"} ${active ? fillClass : ""}`} />
            {arr.length > 0 && <span className={sm ? "text-[11px]" : ""}>{arr.length}</span>}
          </button>
        );
      })}
    </>
  );
}

export function CommentsSection({ threadId, title, className, innerClassName }: Props) {
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const { user, isAdmin } = useUserAuth();
  const { comments, loading } = useComments(threadId);

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedAll, setExpandedAll] = useState(false);
  const [deepLinkTick, setDeepLinkTick] = useState(0);
  // Split into top-level + replies map (declared BEFORE the scroll effect
  // because the effect's deps reference `topLevel` — TDZ would crash render).
  const { topLevel, repliesByParent, totalCount } = useMemo(() => {
    const visible = isAdmin ? comments : comments.filter((c) => !c.hidden);
    const top: Comment[] = [];
    const replies = new Map<string, Comment[]>();
    for (const c of visible) {
      if (c.parentId) {
        const arr = replies.get(c.parentId) ?? [];
        arr.push(c);
        replies.set(c.parentId, arr);
      } else {
        top.push(c);
      }
    }
    // Replies oldest-first (conversation order)
    for (const arr of replies.values()) arr.sort((a, b) => a.createdAt - b.createdAt);
    return { topLevel: top, repliesByParent: replies, totalCount: visible.length };
  }, [comments, isAdmin]);

  const shownTop = expandedAll ? topLevel : topLevel.slice(0, COLLAPSE_AFTER);
  const canCollapse = topLevel.length > COLLAPSE_AFTER;

  // Scroll to a specific comment/reply when the URL has #comment-<id>.
  // Keep this intentionally light: no document.body MutationObserver and no
  // repeated same-page event loop. The old observer watched every DOM/style
  // change in the whole page, then the highlight classes triggered it again,
  // which could create long jank/freezes on comment pages.
  const highlightedRef = useRef<string | null>(null);
  const targetCommentId = useMemo(() => getCommentHashId(), [deepLinkTick]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const restartDeepLink = () => {
      highlightedRef.current = null;
      setDeepLinkTick((value) => value + 1);
    };
    window.addEventListener("hashchange", restartDeepLink);
    return () => window.removeEventListener("hashchange", restartDeepLink);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const initialId = getCommentHashId();
    if (!initialId) return;
    if (highlightedRef.current === initialId) return;

    const targetComment = comments.find((c) => c.id === initialId);
    if (targetComment) {
      const topId = targetComment.parentId ?? targetComment.id;
      const topIdx = topLevel.findIndex((c) => c.id === topId);
      if (topIdx >= COLLAPSE_AFTER) setExpandedAll(true);
    }

    let stopped = false;
    let tries = 0;
    const timers: number[] = [];

    const HIGHLIGHT_CLASSES = ["!bg-primary/10", "!border-primary"] as const;

    const cleanupHighlight = (id: string) => {
      timers.push(window.setTimeout(() => {
        const latest = getVisibleCommentElement(id);
        const bubble = latest?.querySelector<HTMLElement>("[data-comment-bubble]");
        bubble?.classList.remove(...HIGHLIGHT_CLASSES);
      }, 3500));
    };

    const tick = () => {
      if (stopped) return;
      const id = getCommentHashId();
      if (!id) return;

      const currentTarget = comments.find((c) => c.id === id);
      if (currentTarget) {
        const topId = currentTarget.parentId ?? currentTarget.id;
        const topIdx = topLevel.findIndex((c) => c.id === topId);
        if (topIdx >= COLLAPSE_AFTER) setExpandedAll(true);
      }

      const el = getVisibleCommentElement(id);
      if (el) {
        highlightedRef.current = id;
        window.requestAnimationFrame(() => {
          scrollToElement(el, "auto");
          const bubble = el.querySelector<HTMLElement>("[data-comment-bubble]");
          bubble?.classList.add(...HIGHLIGHT_CLASSES);
          clearStoredCommentDeepLink(id);
          cleanupHighlight(id);
        });
        return;
      }

      if (tries === 0) {
        const commentsEl = getVisibleElementById("comments");
        if (commentsEl) scrollToElement(commentsEl, "auto");
      }

      tries += 1;
      if (tries < DEEPLINK_MAX_TRIES) {
        timers.push(window.setTimeout(tick, DEEPLINK_RETRY_MS));
      }
    };

    timers.push(window.setTimeout(tick, 80));
    return () => {
      stopped = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [comments, topLevel, deepLinkTick]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    const value = text.trim();
    if (!value) return;
    setSending(true);
    setError(null);
    const res = await postComment({
      threadId,
      threadTitle: title,
      uid: user.id,
      displayName: user.displayName,
      avatar: user.avatar ?? null,
      text: value,
    });
    setSending(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setText("");
  }

  return (
    <section
      aria-labelledby="comments-title"
      className={cn("container mx-auto px-4 py-8", className)}
      id="comments"
    >
      <div className={cn("bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-card animate-fade-in", innerClassName)}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className="grid place-items-center w-10 h-10 rounded-xl shadow-glow"
            style={{ background: "var(--gradient-primary)" }}
          >
            <MessageCircle className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="comments-title" className="text-xl sm:text-2xl font-extrabold truncate">
              {t("\u0627\u0644\u062a\u0639\u0644\u064a\u0642\u0627\u062a", "Comments", "Commentaires")}{" "}
              <span className="text-base text-muted-foreground font-normal">({totalCount})</span>
            </h2>
            <p className="text-xs text-muted-foreground truncate">{title}</p>
          </div>
        </div>

        {/* Composer */}
        {user ? (
          <form onSubmit={submit} className="mb-6">
            <div className="flex gap-3 items-start">
              <Avatar name={user.displayName} src={user.avatar} />
              <div className="flex-1">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={t(
                    "شارك رأيك… (اضغط Ctrl+Enter للإرسال)",
                    "Share your thoughts… (Ctrl+Enter to send)",
                  )}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") void submit(e as unknown as React.FormEvent);
                  }}
                  className="w-full resize-none rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 px-4 py-3 text-sm transition"
                />
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <div className="text-xs text-muted-foreground">
                    {text.length}/2000
                    {error && <span className="ms-3 text-red-500 font-semibold">{error}</span>}
                  </div>
                  <button
                    type="submit"
                    disabled={sending || !text.trim()}
                    className="btn-primary inline-flex items-center gap-2 !px-5 !py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t("\u0625\u0631\u0633\u0627\u0644", "Send", "Envoyer")}
                  </button>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="mb-6 rounded-xl border border-dashed border-border bg-background/60 p-5 text-center">
            <UserIcon className="h-6 w-6 mx-auto text-muted-foreground" aria-hidden />
            <p className="mt-2 text-sm">
              {t(
                "سجّل الدخول لكتابة تعليق ومشاركة رأيك.",
                "Sign in to write a comment and share your opinion.",
              )}
            </p>
            <div className="mt-3 flex justify-center gap-2 flex-wrap">
              <Link to="/login" className="btn-primary inline-flex !px-5 !py-2 text-sm">
                {t("\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644", "Sign in", "Connexion")}
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center px-5 py-2 rounded-lg border border-border hover:bg-accent text-sm"
              >
                {t("\u0625\u0646\u0634\u0627\u0621 \u062d\u0633\u0627\u0628", "Create account", "S'inscrire")}
              </Link>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 mx-auto animate-spin" />
          </div>
        ) : topLevel.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            {t("لا توجد تعليقات بعد. كن أوّل من يعلّق!", "No comments yet. Be the first!")}
          </div>
        ) : (
          <>
            <ul className="space-y-4">
              {shownTop.map((c) => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  replies={repliesByParent.get(c.id) ?? []}
                  threadId={threadId}
                  threadTitle={title}
                  currentUserId={user?.id ?? null}
                  currentUserName={user?.displayName ?? ""}
                  currentUserAvatar={user?.avatar ?? null}
                  isAdmin={isAdmin}
                  isAr={isAr}
                  targetCommentId={targetCommentId}
                />
              ))}
            </ul>
            {canCollapse && (
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => setExpandedAll((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border hover:bg-accent text-sm font-semibold"
                >
                  {expandedAll ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t("طي التعليقات", "Collapse comments")}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t(
                        `عرض كل التعليقات (${topLevel.length})`,
                        `Show all comments (${topLevel.length})`,
                      )}
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function CommentItem({
  comment,
  replies,
  threadId,
  threadTitle,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  isAdmin,
  isAr,
  targetCommentId,
}: {
  comment: Comment;
  replies: Comment[];
  threadId: string;
  threadTitle: string;
  currentUserId: string | null;
  currentUserName: string;
  currentUserAvatar: string | null;
  isAdmin: boolean;
  isAr: boolean;
  targetCommentId: string | null;
}) {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const [busy, setBusy] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySending, setReplySending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const isOwner = currentUserId === comment.uid;
  // Keep comment threads lightweight. Avoid opening one live profile listener
  // per commenter/reply; large threads can otherwise create heavy network
  // pressure and UI jank. Use the profile snapshot already stored on the
  // comment, with the current user's fresh avatar as a local override.
  const displayName = isOwner && currentUserName ? currentUserName : comment.displayName;
  const displayAvatar = isOwner
    ? currentUserAvatar ?? comment.avatar ?? undefined
    : comment.avatar ?? undefined;

  async function onDelete() {
    if (!confirm(t("حذف التعليق؟", "Delete this comment?"))) return;
    setBusy(true);
    try { await deleteComment(comment.id); } finally { setBusy(false); }
  }
  async function onToggleHide() {
    setBusy(true);
    try { await setCommentHidden(comment.id, !comment.hidden); } finally { setBusy(false); }
  }
  async function submitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUserId) return;
    const value = replyText.trim();
    if (!value) return;
    setReplySending(true);
    setReplyError(null);
    const res = await postComment({
      threadId,
      threadTitle,
      uid: currentUserId,
      displayName: currentUserName,
      avatar: currentUserAvatar,
      text: value,
      parentId: comment.id,
    });
    setReplySending(false);
    if (!res.ok) {
      setReplyError(res.error);
      return;
    }
    setReplyText("");
    setShowReplyBox(false);
    setShowReplies(true);
  }

  async function submitEdit() {
    if (!currentUserId) return;
    const value = editText.trim();
    if (!value || value === comment.text) { setIsEditing(false); return; }
    setEditSaving(true);
    setEditError(null);
    const res = await editComment(comment.id, currentUserId, value);
    setEditSaving(false);
    if (!res.ok) { setEditError(res.error); return; }
    setIsEditing(false);
  }

  useEffect(() => {
    if (!targetCommentId || replies.length === 0) return;
    if (replies.some((reply) => reply.id === targetCommentId)) setShowReplies(true);
  }, [replies, targetCommentId]);

  return (
    <li id={`comment-${comment.id}`} className={`flex gap-3 items-start animate-fade-in scroll-mt-24 ${comment.hidden ? "opacity-60" : ""}`}>
      <Avatar name={displayName} src={displayAvatar} />
      <div className="flex-1 min-w-0">
        <div data-comment-bubble className="rounded-xl bg-background border border-border p-3 sm:p-4 transition-colors duration-500">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-sm">{displayName}</span>
            <span className="text-[11px] text-muted-foreground">{timeAgo(comment.createdAt, isAr)}</span>
            {comment.editedAt && (
              <span className="text-[10px] text-muted-foreground italic">
                · {t("معدّل", "edited")}
              </span>
            )}
            {comment.hidden && (
              <span className="text-[11px] px-2 py-0.5 rounded bg-yellow-500/15 text-yellow-600 border border-yellow-500/30">
                {t("مخفي", "Hidden")}
              </span>
            )}
          </div>
          {isEditing ? (
            <div>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full resize-none rounded-lg bg-card border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 px-3 py-2 text-sm"
              />
              <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
                <div className="text-xs text-red-500 font-semibold">{editError}</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setIsEditing(false); setEditText(comment.text); setEditError(null); }}
                    className="px-3 py-1.5 rounded-md border border-border hover:bg-accent text-xs inline-flex items-center gap-1"
                  >
                    <XIcon className="h-3.5 w-3.5" /> {t("\u0625\u0644\u063a\u0627\u0621", "Cancel", "Annuler")}
                  </button>
                  <button
                    type="button"
                    onClick={submitEdit}
                    disabled={editSaving || !editText.trim()}
                    className="btn-primary inline-flex items-center gap-1.5 !px-3 !py-1.5 text-xs disabled:opacity-50"
                  >
                    {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    {t("\u062d\u0641\u0638", "Save", "Enregistrer")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">{comment.text}</p>
          )}
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-1 mt-1.5 text-xs flex-wrap">
          <ReactionBar
            comment={comment}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            currentUserAvatar={currentUserAvatar}
            isAr={isAr}
          />
          {currentUserId && (
            <button
              type="button"
              onClick={() => setShowReplyBox((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent"
            >
              <Reply className="h-3.5 w-3.5" /> {t("\u0631\u062f", "Reply", "R\u00e9pondre")}
            </button>
          )}
          {replies.length > 0 && (
            <button
              type="button"
              onClick={() => setShowReplies((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent text-primary font-semibold"
            >
              {showReplies ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showReplies
                ? t("إخفاء الردود", "Hide replies")
                : t(`عرض الردود (${replies.length})`, `Show replies (${replies.length})`)}
            </button>
          )}
          {(isOwner || isAdmin) && (
            <>
              {isAdmin && !isOwner && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-primary">
                  <Shield className="h-3 w-3" /> {t("أدمن", "Admin")}
                </span>
              )}
              {isOwner && !isEditing && (
                <button
                  type="button"
                  onClick={() => { setEditText(comment.text); setIsEditing(true); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent"
                >
                  <Pencil className="h-3.5 w-3.5" /> {t("\u062a\u0639\u062f\u064a\u0644", "Edit", "Modifier")}
                </button>
              )}
              {isAdmin && (
                <button
                  type="button"
                  onClick={onToggleHide}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent"
                >
                  {comment.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {comment.hidden ? t("إظهار", "Show") : t("إخفاء", "Hide")}
                </button>
              )}
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-destructive/10 text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" /> {t("\u062d\u0630\u0641", "Delete", "Supprimer")}
              </button>
            </>
          )}
        </div>


        {/* Reply composer */}
        {showReplyBox && currentUserId && (
          <form onSubmit={submitReply} className="mt-3 flex gap-2 items-start">
            <Avatar name={currentUserName} src={currentUserAvatar ?? undefined} />
            <div className="flex-1">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder={t("اكتب رداً…", "Write a reply…")}
                className="w-full resize-none rounded-lg bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 px-3 py-2 text-sm"
              />
              <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  {replyError && <span className="text-red-500 font-semibold">{replyError}</span>}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowReplyBox(false); setReplyText(""); }}
                    className="px-3 py-1.5 rounded-md border border-border hover:bg-accent text-xs"
                  >
                    {t("\u0625\u0644\u063a\u0627\u0621", "Cancel", "Annuler")}
                  </button>
                  <button
                    type="submit"
                    disabled={replySending || !replyText.trim()}
                    className="btn-primary inline-flex items-center gap-1.5 !px-3 !py-1.5 text-xs disabled:opacity-50"
                  >
                    {replySending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {t("\u0625\u0631\u0633\u0627\u0644", "Send", "Envoyer")}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* Replies list */}
        {showReplies && replies.length > 0 && (
          <ul className="mt-3 space-y-3 ps-4 border-s-2 border-border">
            {replies.map((r) => (
              <ReplyItem
                key={r.id}
                comment={r}
                currentUserId={currentUserId}
                currentUserName={currentUserName}
                currentUserAvatar={currentUserAvatar}
                isAdmin={isAdmin}
                isAr={isAr}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function ReplyItem({
  comment,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  isAdmin,
  isAr,
}: {
  comment: Comment;
  currentUserId: string | null;
  currentUserName: string;
  currentUserAvatar: string | null;
  isAdmin: boolean;
  isAr: boolean;
}) {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const [busy, setBusy] = useState(false);
  const isOwner = currentUserId === comment.uid;
  const displayName = isOwner && currentUserName ? currentUserName : comment.displayName;
  const displayAvatar = isOwner
    ? currentUserAvatar ?? comment.avatar ?? undefined
    : comment.avatar ?? undefined;

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  async function onDelete() {
    if (!confirm(t("حذف الرد؟", "Delete this reply?"))) return;
    setBusy(true);
    try { await deleteComment(comment.id); } finally { setBusy(false); }
  }
  async function onToggleHide() {
    setBusy(true);
    try { await setCommentHidden(comment.id, !comment.hidden); } finally { setBusy(false); }
  }
  async function submitEdit() {
    if (!currentUserId) return;
    const value = editText.trim();
    if (!value || value === comment.text) { setIsEditing(false); return; }
    setEditSaving(true);
    setEditError(null);
    const res = await editComment(comment.id, currentUserId, value);
    setEditSaving(false);
    if (!res.ok) { setEditError(res.error); return; }
    setIsEditing(false);
  }

  return (
    <li id={`comment-${comment.id}`} className={`flex gap-2 items-start animate-fade-in scroll-mt-24 ${comment.hidden ? "opacity-60" : ""}`}>
      <Avatar name={displayName} src={displayAvatar} />
      <div className="flex-1 min-w-0">
        <div data-comment-bubble className="rounded-lg bg-background border border-border p-2.5 sm:p-3 transition-colors duration-500">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-bold text-xs">{displayName}</span>
            <span className="text-[10px] text-muted-foreground">{timeAgo(comment.createdAt, isAr)}</span>
            {comment.editedAt && (
              <span className="text-[9px] text-muted-foreground italic">· {t("معدّل", "edited")}</span>
            )}
            {comment.hidden && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-600 border border-yellow-500/30">
                {t("مخفي", "Hidden")}
              </span>
            )}
          </div>
          {isEditing ? (
            <div>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={2}
                maxLength={2000}
                className="w-full resize-none rounded-md bg-card border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 px-2 py-1.5 text-[13px]"
              />
              <div className="flex items-center justify-end gap-2 mt-1">
                {editError && <span className="text-[10px] text-red-500 font-semibold">{editError}</span>}
                <button
                  type="button"
                  onClick={() => { setIsEditing(false); setEditText(comment.text); setEditError(null); }}
                  className="px-2 py-1 rounded border border-border hover:bg-accent text-[11px] inline-flex items-center gap-1"
                >
                  <XIcon className="h-3 w-3" /> {t("\u0625\u0644\u063a\u0627\u0621", "Cancel", "Annuler")}
                </button>
                <button
                  type="button"
                  onClick={submitEdit}
                  disabled={editSaving || !editText.trim()}
                  className="btn-primary inline-flex items-center gap-1 !px-2 !py-1 text-[11px] disabled:opacity-50"
                >
                  {editSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  {t("\u062d\u0641\u0638", "Save", "Enregistrer")}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[13px] whitespace-pre-wrap leading-relaxed break-words">{comment.text}</p>
          )}
        </div>
        <div className="flex items-center gap-1 mt-1 text-xs flex-wrap">
          <ReactionBar
            comment={comment}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            currentUserAvatar={currentUserAvatar}
            size="sm"
            isAr={isAr}
          />
          {isOwner && !isEditing && (
            <button
              type="button"
              onClick={() => { setEditText(comment.text); setIsEditing(true); }}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-accent"
              title={t("\u062a\u0639\u062f\u064a\u0644", "Edit", "Modifier")}
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {isAdmin && (
            <button type="button" onClick={onToggleHide} disabled={busy} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-accent">
              {comment.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            </button>
          )}
          {(isOwner || isAdmin) && (
            <button type="button" onClick={onDelete} disabled={busy} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-destructive/10 text-destructive">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
